import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import PosLayout from "../../components/layouts/posLayout.comp";
import {
  listWarehouseTransferNotes,
  transferNoteIdString,
  type WarehouseTransferListView,
} from "../../core/warehouseTransferNote.core";
import { useTranslation } from "../../core/translation.core";
import useGetRetailShopId from "../../hooks/useGetRetailShopId.hook";
import type { WarehouseTransferNote } from "../../types/warehouseTransferNote";

export default function WarehouseTransferListPage() {
  const { t, msg } = useTranslation();
  return (
    <PosLayout title={t(msg.warehouseTransfer.title)} showback>
      <Content />
    </PosLayout>
  );
}

function statusLabel(key: string, t: (x: any) => string, msg: any): string {
  switch (key) {
    case "draft":
      return t(msg.warehouseTransfer.statusDraft);
    case "submitted":
      return t(msg.warehouseTransfer.statusSubmitted);
    case "picking":
      return t(msg.warehouseTransfer.statusPicking);
    case "onDelivery":
      return t(msg.warehouseTransfer.statusOnDelivery);
    case "stockInCompleted":
      return t(msg.warehouseTransfer.statusStockInCompleted);
    default:
      return key;
  }
}

function TransferListSection({
  title,
  emptyText,
  view,
  retailId,
  listRole,
}: {
  title: string;
  emptyText: string;
  view: WarehouseTransferListView;
  retailId: string;
  listRole: string;
}) {
  const { t, msg } = useTranslation();

  const { data, isPending } = useQuery({
    enabled: !!retailId,
    queryKey: ["warehouseTransferList", retailId, view],
    queryFn: () => listWarehouseTransferNotes({ view, paging: { page: 1, size: 100 } }),
  });

  return (
    <section data-type="section" className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {isPending && <p className="text-sm opacity-70">{t(msg.form.loading)}</p>}
      {!isPending && data && data.list.length === 0 && (
        <p className="text-sm opacity-80">{emptyText}</p>
      )}
      {!isPending && data && data.list.length > 0 && (
        <ul className="space-y-2" data-role={listRole}>
          {data.list.map((row) => (
            <TransferListRow key={transferNoteIdString(row.id)} row={row} t={t} msg={msg} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TransferListRow({
  row,
  t,
  msg,
}: {
  row: WarehouseTransferNote;
  t: (x: any) => string;
  msg: any;
}) {
  return (
    <li>
      <Link
        href={`/warehouseTransfer/${transferNoteIdString(row.id)}`}
        className="block rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-her-darkblue2 p-4 hover:border-her-blue"
        data-role={`warehouse-transfer-row-${transferNoteIdString(row.id)}`}
      >
        <div className="flex flex-wrap justify-between gap-2">
          <span className="font-semibold">{row.transferNumber}</span>
          <span className="text-sm rounded-full bg-her-lightgray dark:bg-her-bluegray px-3 py-0.5">
            {statusLabel(row.status, t, msg)}
          </span>
        </div>
        <p className="text-xs opacity-70 mt-1">
          {row.items.length} {t(msg.product.product)}
        </p>
      </Link>
    </li>
  );
}

function Content() {
  const { t, msg } = useTranslation();
  const retailId = useGetRetailShopId() ?? "";

  return (
    <main className="dark:text-white space-y-8 p-2 lg:p-4 max-w-4xl" data-role="warehouse-transfer-list-page">
      <section data-type="section" className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold tracking-tight">{t(msg.warehouseTransfer.title)}</h1>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Link
              href="/purchaseOrder"
              className="rounded-lg border border-her-pumpkin bg-her-pumpkin px-4 py-2 font-medium text-center text-white hover:border-her-orange hover:bg-her-orange"
              data-role="purchase-order-list-link"
            >
              {t(msg.purchaseOrder.title)}
            </Link>
            <Link
              href="/warehouseTransfer/new"
              className="rounded-lg border border-her-lightSeaGreen bg-her-lightSeaGreen px-4 py-2 font-medium text-center text-white hover:border-her-turquoiseSurf hover:bg-her-turquoiseSurf"
              data-role="warehouse-transfer-new-request-link"
            >
              {t(msg.warehouseTransfer.newRequest)}
            </Link>
            <Link
              href="/warehouseTransfer/new?mode=return"
              className="rounded-lg bg-her-blue text-white px-4 py-2 font-medium text-center"
              data-role="warehouse-transfer-new-return-link"
            >
              {t(msg.warehouseTransfer.newReturn)}
            </Link>
          </div>
        </div>
      </section>

      <TransferListSection
        title={t(msg.warehouseTransfer.sectionIncomingArrivals)}
        emptyText={t(msg.warehouseTransfer.noIncomingArrivals)}
        view="incomingOnDelivery"
        retailId={retailId}
        listRole="warehouse-transfer-list-incoming"
      />

      <TransferListSection
        title={t(msg.warehouseTransfer.sectionPendingReturns)}
        emptyText={t(msg.warehouseTransfer.noPendingReturns)}
        view="outgoingFromRetailStock"
        retailId={retailId}
        listRole="warehouse-transfer-list-pending-returns"
      />

      <TransferListSection
        title={t(msg.warehouseTransfer.sectionDrafts)}
        emptyText={t(msg.warehouseTransfer.noDrafts)}
        view="outgoingDrafts"
        retailId={retailId}
        listRole="warehouse-transfer-list-drafts"
      />
    </main>
  );
}
